// frontend/src/utils/apiClient.js
/**
 * Centralized API Client with automatic token refresh and session expiry handling.
 * On 401: attempts Firebase token refresh and retries once before redirecting.
 * Never redirects if already on the login page.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Attempt to get a fresh Firebase ID token and store it in localStorage.
 * Returns the new token string, or null if refresh failed.
 */
async function tryRefreshToken() {
  try {
    const { getAuth } = await import('firebase/auth');
    const firebaseAuth = getAuth();
    const user = firebaseAuth.currentUser;
    if (!user) return null;
    const freshToken = await user.getIdToken(true); // force refresh
    localStorage.setItem('hopeSaccoToken', freshToken);
    console.log('✅ Firebase token refreshed successfully');
    return freshToken;
  } catch (err) {
    console.warn('Token refresh failed:', err?.message || err);
    return null;
  }
}

/**
 * Clear authentication and redirect to login.
 * Does nothing if already on the login page (prevents blink during login flow).
 */
function handleSessionExpiry() {
  if (window.location.pathname === '/login') {
    // Already on login page — don't redirect again (prevents blink)
    return;
  }

  console.log('🔒 Session expired - clearing auth and redirecting to login');

  // Clear all auth data
  localStorage.removeItem('hopeSaccoToken');
  localStorage.removeItem('hopeSaccoUser');
  localStorage.removeItem('lastActivityTime');

  // Store current path so we can redirect back after login
  const currentPath = window.location.pathname;
  if (currentPath !== '/login' && currentPath !== '/signup') {
    sessionStorage.setItem('redirectAfterLogin', currentPath);
  }

  // Redirect to login
  window.location.href = '/login';
}

/**
 * Make authenticated API request.
 * On 401: tries token refresh and retries once before giving up.
 */
export async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem('hopeSaccoToken');

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);

    // Handle session expiry (401)
    if (response.status === 401) {
      console.log('🔄 Got 401 - attempting token refresh before giving up...');

      // Try to get a fresh Firebase token
      const freshToken = await tryRefreshToken();

      if (freshToken) {
        // Retry the original request once with the new token
        const retryConfig = {
          ...config,
          headers: {
            ...config.headers,
            'Authorization': `Bearer ${freshToken}`,
          },
        };

        const retryResponse = await fetch(`${API_URL}${endpoint}`, retryConfig);

        if (retryResponse.status === 401) {
          // Still 401 after refresh — session is genuinely expired
          handleSessionExpiry();
          throw new Error('Session expired. Redirecting to login...');
        }

        return retryResponse;
      }

      // No Firebase user or refresh failed — session is gone
      handleSessionExpiry();
      throw new Error('Session expired. Redirecting to login...');
    }

    return response;
  } catch (error) {
    if (error.message === 'Session expired. Redirecting to login...') {
      throw error;
    }
    throw error;
  }
}

/**
 * Convenience methods for different HTTP verbs
 */
export const api = {
  get: (endpoint, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'GET',
    });
  },

  post: (endpoint, data, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  put: (endpoint, data, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: (endpoint, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'DELETE',
    });
  },

  patch: (endpoint, data, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Check if token is still valid — lightweight local check only.
 * Returns true if a token exists in localStorage, false otherwise.
 * No backend call — Firebase handles token validity automatically.
 */
export async function validateToken() {
  const token = localStorage.getItem('hopeSaccoToken');
  return !!token;
}

export default api;
